// Route: /data-sources — admin-gated list page for data sources.
// Shows all non-deleted data sources with an upload wizard to add new ones.
// Each data source row is expandable to show its Object Type mappings (multi-mapping support).

import { useState } from "react";
import { Navigate } from "react-router-dom";
import { UploadCloud, MoreHorizontal, Trash2, ChevronDown, ChevronRight } from "lucide-react";
import { useRole } from "@/hooks/useRole";
import { useDataSources, useSoftDeleteDataSource, useUploaderNames } from "@/hooks/useDataSources";
import { PageHeader } from "@/components/layout/PageHeader";
import { EmptyState } from "@/components/feedback/EmptyState";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { UploadWizard } from "@/components/ontology/UploadWizard";
import { DataSourceMappingsList } from "@/components/ontology/DataSourceMappingsList";
import type { DataSource } from "@/types/ontology";

function formatDateTime(iso: string): string {
  return new Date(iso).toLocaleString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

export default function DataSourcesPage() {
  const { isAdmin, isLoading: roleLoading } = useRole();
  const { dataSources, isLoading } = useDataSources();
  const { resolve: resolveUploaderName } = useUploaderNames(
    dataSources.map((ds) => ds.uploaded_by)
  );
  const softDelete = useSoftDeleteDataSource();

  const [wizardOpen, setWizardOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<DataSource | null>(null);
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());

  const toggleRow = (id: string) => {
    setExpandedRows((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  if (roleLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin w-8 h-8 border-4 border-primary border-t-transparent rounded-full" />
      </div>
    );
  }

  if (!isAdmin) {
    return <Navigate to="/" replace />;
  }

  return (
    <main className="w-full flex flex-col">
      <PageHeader
        title="Data Sources"
        subtitle="Upload and manage CSV data sources for the ontology."
        actions={
          <Button onClick={() => setWizardOpen(true)} size="sm">
            <UploadCloud className="w-3.5 h-3.5 mr-1.5 flex-shrink-0" />
            Upload CSV
          </Button>
        }
      />

      <div className="px-6 py-6 space-y-8">
        {isLoading ? (
          <DataSourcesTableSkeleton />
        ) : dataSources.length === 0 ? (
          <EmptyState
            icon={UploadCloud}
            title="No data sources yet"
            description="Upload a CSV to get started."
            action={
              <Button onClick={() => setWizardOpen(true)}>
                <UploadCloud className="w-3.5 h-3.5 mr-1.5 flex-shrink-0" />
                Upload CSV
              </Button>
            }
          />
        ) : (
          <div className="rounded-lg border border-border overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow className="hover:bg-transparent">
                  <TableHead className="w-8" />
                  <TableHead>Name</TableHead>
                  <TableHead>Kind</TableHead>
                  <TableHead>Rows</TableHead>
                  <TableHead>Uploaded by</TableHead>
                  <TableHead>Uploaded at</TableHead>
                  <TableHead className="w-12" />
                </TableRow>
              </TableHeader>
              <TableBody>
                {dataSources.map((ds) => (
                  <>
                    <DataSourceRow
                      key={ds.id}
                      dataSource={ds}
                      uploaderName={resolveUploaderName(ds.uploaded_by)}
                      expanded={expandedRows.has(ds.id)}
                      onToggleExpand={() => toggleRow(ds.id)}
                      onDelete={() => setDeleteTarget(ds)}
                    />
                    {expandedRows.has(ds.id) && (
                      <TableRow
                        key={`${ds.id}-mappings`}
                        className="hover:bg-transparent bg-muted/20"
                      >
                        <TableCell />
                        <TableCell colSpan={5} className="pb-3 pt-1">
                          <DataSourceMappingsList dataSource={ds} />
                        </TableCell>
                        <TableCell />
                      </TableRow>
                    )}
                  </>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </div>

      {/* Upload wizard */}
      <UploadWizard open={wizardOpen} onClose={() => setWizardOpen(false)} />

      {/* Delete confirmation */}
      <AlertDialog open={!!deleteTarget} onOpenChange={(open) => !open && setDeleteTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete data source?</AlertDialogTitle>
            <AlertDialogDescription>
              <strong>{deleteTarget?.name}</strong> will be soft-deleted. Existing materializations
              are not affected but no new materializations can be started.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={() => {
                if (deleteTarget) {
                  softDelete.mutate(deleteTarget.id);
                  setDeleteTarget(null);
                }
              }}
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </main>
  );
}

// ── Sub-components ──────────────────────────────────────────────────────────

interface DataSourceRowProps {
  dataSource: DataSource;
  uploaderName: string;
  expanded: boolean;
  onToggleExpand: () => void;
  onDelete: () => void;
}

function DataSourceRow({
  dataSource,
  uploaderName,
  expanded,
  onToggleExpand,
  onDelete,
}: DataSourceRowProps) {
  return (
    <TableRow className="hover:bg-muted/40">
      <TableCell className="w-8 pr-0">
        <Button
          variant="ghost"
          size="icon"
          className="h-6 w-6"
          onClick={onToggleExpand}
          aria-label={expanded ? "Collapse mappings" : "Expand mappings"}
          data-testid={`expand-${dataSource.id}`}
        >
          {expanded ? (
            <ChevronDown className="w-3.5 h-3.5" />
          ) : (
            <ChevronRight className="w-3.5 h-3.5" />
          )}
        </Button>
      </TableCell>
      <TableCell className="font-medium">{dataSource.name}</TableCell>
      <TableCell>
        <span className="text-xs font-mono text-muted-foreground">{dataSource.kind}</span>
      </TableCell>
      <TableCell>{dataSource.row_count ?? "—"}</TableCell>
      <TableCell className="text-muted-foreground text-sm">{uploaderName}</TableCell>
      <TableCell className="text-muted-foreground text-sm">
        {formatDateTime(dataSource.uploaded_at)}
      </TableCell>
      <TableCell>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="icon" className="h-8 w-8">
              <MoreHorizontal className="w-4 h-4" />
              <span className="sr-only">Actions</span>
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuSeparator />
            <DropdownMenuItem
              className="gap-2 text-destructive focus:text-destructive"
              onClick={onDelete}
            >
              <Trash2 className="w-3.5 h-3.5" />
              Delete
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </TableCell>
    </TableRow>
  );
}

function DataSourcesTableSkeleton() {
  return (
    <div className="rounded-lg border border-border overflow-hidden">
      <Table>
        <TableHeader>
          <TableRow className="hover:bg-transparent">
            <TableHead className="w-8" />
            <TableHead>Name</TableHead>
            <TableHead>Kind</TableHead>
            <TableHead>Rows</TableHead>
            <TableHead>Uploaded by</TableHead>
            <TableHead>Uploaded at</TableHead>
            <TableHead className="w-12" />
          </TableRow>
        </TableHeader>
        <TableBody>
          {Array.from({ length: 4 }).map((_, i) => (
            <TableRow key={i}>
              <TableCell>
                <Skeleton className="h-4 w-6" />
              </TableCell>
              <TableCell>
                <Skeleton className="h-4 w-40" />
              </TableCell>
              <TableCell>
                <Skeleton className="h-4 w-16" />
              </TableCell>
              <TableCell>
                <Skeleton className="h-4 w-12" />
              </TableCell>
              <TableCell>
                <Skeleton className="h-4 w-24" />
              </TableCell>
              <TableCell>
                <Skeleton className="h-4 w-24" />
              </TableCell>
              <TableCell>
                <Skeleton className="h-4 w-8" />
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}
